import React, { useEffect, useState } from 'react';
import { Button, Dropdown, MenuProps, Table, Menu, Modal } from 'antd';
import { SoundOutlined, DeleteOutlined, CopyOutlined, ApiOutlined, CheckCircleTwoTone, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';

import { AdminRoutes } from '../../../_router/admin.routes';
import { AiChatSettingsService, i18n, NavigationService } from '../../../../../common';
import { AiChatSettingsInterface } from '../../../../../common/interfaces/aiChatSettings/aiChatSettings.interface';

function AiChatSettingsTablePageComponent() {
  const [aiChatSettings, setAiChatSettings] = useState<AiChatSettingsInterface[]>([]);

  useEffect(() => {
    fetchAiChatSettings();
  }, []);

  const fetchAiChatSettings = () => {
    AiChatSettingsService.getAll().subscribe((response) => {
      setAiChatSettings(response.data);
    });
  };

  const getActions = (record: AiChatSettingsInterface) => {
    const items: MenuProps['items'] = [
      {
        label: 'Kloniraj',
        key: '1',
        icon: <CopyOutlined />,
        onClick: () => cloneItem(record),
      },
      {
        label: record.attributes.isPublic ? 'Ukloni JAVNO' : 'Postavi JAVNO',
        key: '2',
        icon: <SoundOutlined />,
        onClick: () => publicToggle(record),
      },
      {
        label: record.attributes.active ? 'Deaktiviraj' : 'Aktiviraj',
        key: '3',
        icon: <ApiOutlined />,
        onClick: () => toggleActive(record),
      },
      {
        label: 'Izbriši',
        key: '4',
        icon: <DeleteOutlined />,
        onClick: () => deleteItem(record),
      },
    ];
    return items;
  };

  const columns: ColumnType<AiChatSettingsInterface>[] = [
    {
      title: 'Ime modela',
      key: 'modelName',
      width: 150,
      render: (text: AiChatSettingsInterface) => {
        return text.attributes.modelName;
      },
    },
    {
      title: 'Verzija',
      key: 'modelVersion',
      width: 100,
      render: (text: AiChatSettingsInterface) => {
        return text.attributes.modelVersion;
      },
    },
    {
      title: 'Opis',
      key: 'description',
      render: (text: AiChatSettingsInterface) => {
        return text.attributes.description;
      },
    },
    {
      title: 'JAVNO',
      key: 'isPublic',
      width: 120,
      render: (text: AiChatSettingsInterface) => (
        <div className="center-center">{text.attributes.isPublic ? <CheckCircleTwoTone /> : ''}</div>
      ),
    },
    {
      title: 'Aktivno',
      key: 'active',
      width: 100,
      render: (text: AiChatSettingsInterface) => (
        <div className="center-center">{text.attributes.active ? 'Da' : 'Ne'}</div>
      ),
    },
    {
      title: '',
      width: 100,
      render: (record: AiChatSettingsInterface) => (
        <Dropdown.Button
          type="link"
          overlay={<Menu items={getActions(record)} />}
          onClick={() => NavigationService.navigate(AdminRoutes.AI_CHAT_SETTINGS.fullPath + '/' + record.id)}
        >
          Uredi
        </Dropdown.Button>
      ),
    },
  ];

  const handleAdd = () => {
    NavigationService.navigate(AdminRoutes.AI_CHAT_SETTINGS.fullPath + '/new');
  };

  const publicToggle = (record: AiChatSettingsInterface) => {
    AiChatSettingsService.setPublic(record.id!, !record.attributes.isPublic).subscribe(() => {
      // fetchAiChatSettings();
      window.location.reload();
    });
  };

  const toggleActive = (record: AiChatSettingsInterface) => {
    const { createdDate, modifiedDate, createdBy, modifiedBy, ...attributes } = record.attributes;

    const body: AiChatSettingsInterface = {
      ...record,
      attributes: { ...attributes, active: !attributes.active },
    };
    AiChatSettingsService.update(body).subscribe(() => {
      // setAiChatSettings(aiChatSettings.map((item) => (item.id === record.id ? body : item)));
      window.location.reload();
    });
  };

  const cloneItem = (record: AiChatSettingsInterface) => {
    const { createdDate, modifiedDate, createdBy, modifiedBy, ...attributes } = record.attributes;
    const body: AiChatSettingsInterface = {
      type: 'ai_chat_settings',
      attributes: { ...attributes, modelName: attributes.modelName + ' (copy)', active: false, isPublic: false },
    };
    AiChatSettingsService.create(body).subscribe((response) => {
      setAiChatSettings([...aiChatSettings, response.data]);
    });
  };

  const deleteItem = (record: AiChatSettingsInterface) => {
    Modal.confirm({
      title: 'Da li ste sigurni da želite izbrisati ovu postavku?',
      icon: <ExclamationCircleOutlined />,
      content: 'Nije moguće vratiti postavku nakon brisanja.',
      okText: 'Da',
      cancelText: 'Ne',
      onOk: () => {
        AiChatSettingsService.delete(record.id!).subscribe(() => {
          setAiChatSettings(aiChatSettings.filter((item) => item.id !== record.id));
        });
      },
    });
  };

  const renderError = () => {
    if (aiChatSettings.length === 0 || aiChatSettings.every((item) => !item.attributes.active)) {
      return <div className="error-message">AI chat skriven je za sve korisnike.</div>;
    }

    const publicSettings = aiChatSettings.find((item) => item.attributes.isPublic);
    if (!publicSettings || !publicSettings.attributes.active) {
      return (
        <div className="error-message">
          Trenutno nema aktivnih javnih AI Chat postavki. AI Chat neće biti dostupan za JAVNO korisnicima.
        </div>
      );
    }

    const limitWarnings = getLimitMismatchWarnings();
    if (limitWarnings.length > 0) {
      return (
        <>
          {limitWarnings.map((warning, index) => (
            <div className="error-message" key={index}>
              {warning}
            </div>
          ))}
        </>
      );
    }

    return null;
  };

  const getLimitMismatchWarnings = (): string[] => {
    const publicSettings = aiChatSettings.filter((item) => item.attributes.isPublic);
    if (publicSettings.length < 2) {
      return [];
    }

    const limitAttributes: Array<{
      key: 'monthlyQuestionLimitPremium' | 'monthlyQuestionLimitStandard' | 'monthlyQuestionLimitFree';
      label: string;
    }> = [
      { key: 'monthlyQuestionLimitPremium', label: 'Premium mjesečni limit pitanja' },
      { key: 'monthlyQuestionLimitStandard', label: 'Standard mjesečni limit pitanja' },
      { key: 'monthlyQuestionLimitFree', label: 'Free mjesečni limit pitanja' },
    ];

    const settingLabel = (item: AiChatSettingsInterface) =>
      `${item.attributes.modelName} (${item.attributes.modelVersion})`;

    const warnings: string[] = [];

    limitAttributes.forEach(({ key, label }) => {
      const reference = publicSettings[0];
      const mismatch = publicSettings.find(
        (item) => item.attributes[key] !== reference.attributes[key]
      );

      if (mismatch) {
        warnings.push(
          `Vrijednost "${label}" nije usklađena između javnih postavki "${settingLabel(
            reference
          )}" i "${settingLabel(mismatch)}".`
        );
      }
    });

    return warnings;
  };

  return (
    <div className="ai-chat-settings-page w100-h100">
      <div className="action-container">
        {/* <div className="title">AI Chat postavke</div> */}
        <div></div>
        <Button type="primary" onClick={handleAdd}>
          Dodaj
        </Button>
      </div>

      {renderError()}

      <Table
        dataSource={aiChatSettings}
        columns={columns}
        pagination={false}
        rowKey={(record) => record.id?.toString() ?? ''}
        locale={{
          emptyText: i18n.t('common.noData'),
          triggerDesc: i18n.t('common.sortDesc'),
          triggerAsc: i18n.t('common.sortAsc'),
          cancelSort: i18n.t('common.cancelSort'),
          filterReset: i18n.t('common.reset'),
        }}
      />
    </div>
  );
}

export default AiChatSettingsTablePageComponent;
